import React, { useState, useEffect } from "react";
import { IContact } from "@/interfaces/Icontact";
import { contactsApi } from "@/api/api";
import toast from "react-hot-toast";
import { FiPlus } from "react-icons/fi";
import {
  FaInstagram,
  FaXTwitter,
  FaLinkedin,
  FaFacebook,
  FaYoutube,
  FaGlobe,
} from "react-icons/fa6";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { ensurePhoneNumberFormat } from "@/utils/phoneNumberUtils";

interface IContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  contactToEdit?: IContact;
  setContacts: React.Dispatch<React.SetStateAction<IContact[]>>;
  setSelectedContact: React.Dispatch<React.SetStateAction<IContact | null>>;
  onRefetch?: () => void;
}

export default function ContactModal({
  isOpen,
  onClose,
  mode,
  contactToEdit,
  setContacts,
  setSelectedContact,
  onRefetch,
}: IContactModalProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [about, setAbout] = useState("");
  const [address, setAddress] = useState({
    country: "",
    city: "",
    state: "",
    zipCode: "",
    line1: "",
  });
  const [socialLinks, setSocialLinks] = useState({
    instagram: "",
    twitter: "",
    linkedin: "",
    facebook: "",
    youtube: "",
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [phones, setPhones] = useState<string[]>([""]);
  const [email, setEmail] = useState<string>("");
  const [fullAddress, setFullAddress] = useState("");
  const [useManualAddress, setUseManualAddress] = useState(false);
  const [showFields, setShowFields] = useState<{
    phones: boolean;
    emails: boolean;
    instagram: boolean;
    twitter: boolean;
    linkedin: boolean;
    facebook: boolean;
    youtube: boolean;
    website: boolean;
  }>({
    phones: true,
    emails: true,
    instagram: false,
    twitter: false,
    linkedin: false,
    facebook: false,
    youtube: false,
    website: false,
  });
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [initialContactData, setInitialContactData] = useState<any>(null);

  // Check if form data has changed
  const hasChanges = () => {
    if (!initialContactData) return false;

    return (
      firstName !== initialContactData.firstName ||
      lastName !== initialContactData.lastName ||
      companyName !== initialContactData.companyName ||
      website !== initialContactData.website ||
      about !== initialContactData.about ||
      email !== initialContactData.email ||
      JSON.stringify(phones) !== JSON.stringify(initialContactData.phones) ||
      JSON.stringify(address) !== JSON.stringify(initialContactData.address) ||
      JSON.stringify(socialLinks) !==
        JSON.stringify(initialContactData.socialLinks) ||
      JSON.stringify(showFields) !==
        JSON.stringify(initialContactData.showFields) ||
      avatarFile !== initialContactData.avatarFile
    );
  };

  // Check what specifically changed
  const getChangedFields = () => {
    if (!initialContactData)
      return { basic: false, phones: false, avatar: false };

    const basicChanged =
      firstName !== initialContactData.firstName ||
      lastName !== initialContactData.lastName ||
      companyName !== initialContactData.companyName ||
      website !== initialContactData.website ||
      about !== initialContactData.about ||
      email !== initialContactData.email ||
      JSON.stringify(address) !== JSON.stringify(initialContactData.address) ||
      JSON.stringify(socialLinks) !==
        JSON.stringify(initialContactData.socialLinks) ||
      JSON.stringify(showFields) !==
        JSON.stringify(initialContactData.showFields);

    const phonesChanged =
      JSON.stringify(phones) !== JSON.stringify(initialContactData.phones);
    const avatarChanged = avatarFile !== initialContactData.avatarFile;

    return {
      basic: basicChanged,
      phones: phonesChanged,
      avatar: avatarChanged,
    };
  };

  // Create lists of phones to update and delete, then call APIs
  const handlePhoneChanges = (contactId: string, formattedPhones: string[]) => {
    const operations: Promise<any>[] = [];
    const initialPhonesWithIds = initialContactData?.phonesWithIds || [];
    const currentPhones = formattedPhones.filter(
      (phone) => phone.trim() !== ""
    ); // Remove empty strings

    console.log("handlePhoneChanges debug:", {
      contactId,
      formattedPhones,
      initialPhonesWithIds,
      currentPhones,
      phonesState: phones,
    });

    console.log("Phone change analysis:");
    console.log("Initial phones:", initialPhonesWithIds);
    console.log("Current phones:", currentPhones);

    // 1. Create lists for phones to update and delete
    const phonesToUpdate: Array<{
      id: string;
      newPhone: string;
      oldPhone: string;
    }> = [];
    const phonesToDelete: Array<{ id: string; phone: string }> = [];

    // Compare phones by position - assume phones maintain their relative positions
    const maxLength = Math.max(
      initialPhonesWithIds.length,
      currentPhones.length
    );

    for (let i = 0; i < maxLength; i++) {
      const initialPhone = initialPhonesWithIds[i];
      const currentPhone = currentPhones[i];

      if (
        initialPhone &&
        initialPhone.phone &&
        currentPhone &&
        currentPhone.trim()
      ) {
        // Both exist at this position
        if (initialPhone.phone !== currentPhone.trim()) {
          // Phone number changed - add to update list
          console.log(
            `Adding to update list - phone at position ${i}: "${
              initialPhone.phone
            }" -> "${currentPhone.trim()}"`
          );
          phonesToUpdate.push({
            id: initialPhone.id.toString(),
            newPhone: currentPhone.trim(),
            oldPhone: initialPhone.phone,
          });
        }
      } else if (
        initialPhone &&
        initialPhone.phone &&
        (!currentPhone || !currentPhone.trim())
      ) {
        // Initial phone exists but current phone is empty/deleted - add to delete list
        console.log(
          `Adding to delete list - phone at position ${i}: "${initialPhone.phone}"`
        );
        phonesToDelete.push({
          id: initialPhone.id.toString(),
          phone: initialPhone.phone,
        });
      } else if (
        (!initialPhone || !initialPhone.phone) &&
        currentPhone &&
        currentPhone.trim()
      ) {
        // Current phone exists but initial phone doesn't (new phone added)
        console.log(
          `Adding new phone at position ${i}: "${currentPhone.trim()}"`
        );
        const addOperation = contactsApi
          .addPhoneToContact(contactId, currentPhone.trim())
          .then(() => {
            return {
              type: "addPhone",
              success: true,
              phone: currentPhone.trim(),
            };
          })
          .catch((error) => {
            console.error(`Error adding phone ${currentPhone}:`, error);
            throw error;
          });
        operations.push(addOperation);
      }
    }

    // 2. Call update API for phones to update
    if (phonesToUpdate.length > 0) {
      console.log("Phones to update:", phonesToUpdate);
      phonesToUpdate.forEach(({ id, newPhone, oldPhone }) => {
        const updateOperation = contactsApi
          .updatePhoneOfContact(contactId, id, newPhone)
          .then(() => {
            return {
              type: "updatePhone",
              success: true,
              phoneId: id,
              oldPhone,
              newPhone,
            };
          })
          .catch((error) => {
            console.error(
              `Error updating phone from ${oldPhone} to ${newPhone}:`,
              error
            );
            throw error;
          });
        operations.push(updateOperation);
      });
    }

    // 3. Call delete API for phones to delete
    if (phonesToDelete.length > 0) {
      console.log("Phones to delete:", phonesToDelete);
      phonesToDelete.forEach(({ id, phone }) => {
        const deleteOperation = contactsApi
          .deletePhoneFromContact(contactId, id)
          .then(() => {
            return {
              type: "deletePhone",
              success: true,
              phoneId: id,
              phone,
            };
          })
          .catch((error) => {
            console.error(`Error deleting phone ${phone}:`, error);
            throw error;
          });
        operations.push(deleteOperation);
      });
    }

    return operations;
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showAddMenu && !target.closest(".add-menu-container")) {
        setShowAddMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddMenu]);

  const handleAddressSelect = (addressData: {
    line1: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  }) => {
    setAddress({
      country: addressData.country || address.country,
      city: addressData.city,
      state: addressData.state,
      zipCode: addressData.zipCode,
      line1: addressData.line1,
    });
  };

  // Initialize form data when modal opens or contact changes
  useEffect(() => {
    if (mode === "add") {
      // Reset form for add mode
      setFirstName("");
      setLastName("");
      setCompanyName("");
      setWebsite("");
      setAbout("");
      setAddress({
        country: "",
        city: "",
        state: "",
        zipCode: "",
        line1: "",
      });
      setSocialLinks({
        instagram: "",
        twitter: "",
        linkedin: "",
        facebook: "",
        youtube: "",
      });
      setPhones([""]);
      setEmail("");
      setFullAddress("");
      setUseManualAddress(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      setShowFields({
        phones: true,
        emails: true,
        instagram: false,
        twitter: false,
        linkedin: false,
        facebook: false,
        youtube: false,
        website: false,
      });
    }

    // Store initial contact data for change detection
    setInitialContactData({
      firstName:
        mode === "edit" && contactToEdit ? contactToEdit.first_name || "" : "",
      lastName:
        mode === "edit" && contactToEdit ? contactToEdit.last_name || "" : "",
      companyName:
        mode === "edit" && contactToEdit
          ? contactToEdit.company_name || ""
          : "",
      website:
        mode === "edit" && contactToEdit
          ? contactToEdit.socialLinks?.website || ""
          : "",
      about:
        mode === "edit" && contactToEdit
          ? contactToEdit.note?.content || ""
          : "",
      email:
        mode === "edit" && contactToEdit
          ? contactToEdit.emails && contactToEdit.emails.length > 0
            ? contactToEdit.emails
                .filter((email) => email !== null && email.email)
                .map((email) => email.email)[0] || ""
            : ""
          : "",
      phones:
        mode === "edit" && contactToEdit
          ? contactToEdit.phones && contactToEdit.phones.length > 0
            ? contactToEdit.phones
                .filter((phone) => phone !== null && phone.phone)
                .map((phone) => phone.phone)
            : [""]
          : [""],
      phonesWithIds:
        mode === "edit" && contactToEdit
          ? contactToEdit.phones && contactToEdit.phones.length > 0
            ? contactToEdit.phones
                .filter((phone) => phone !== null && phone.phone)
                .map((phone) => ({ id: phone.id, phone: phone.phone }))
            : []
          : [],
      address:
        mode === "edit" && contactToEdit
          ? {
              country: contactToEdit.address?.country || "",
              city: contactToEdit.address?.city || "",
              state: contactToEdit.address?.state || "",
              zipCode: contactToEdit.address?.zip_code || "",
              line1: contactToEdit.address?.line_1 || "",
            }
          : {
              country: "",
              city: "",
              state: "",
              zipCode: "",
              line1: "",
            },
      socialLinks:
        mode === "edit" && contactToEdit
          ? {
              instagram: contactToEdit.socialLinks?.instagram || "",
              twitter: contactToEdit.socialLinks?.twitter || "",
              linkedin: contactToEdit.socialLinks?.linkedin || "",
              facebook: contactToEdit.socialLinks?.facebook || "",
              youtube: contactToEdit.socialLinks?.youtube || "",
            }
          : {
              instagram: "",
              twitter: "",
              linkedin: "",
              facebook: "",
              youtube: "",
            },
      showFields:
        mode === "edit" && contactToEdit
          ? {
              phones: true,
              emails: true,
              instagram: !!contactToEdit.socialLinks?.instagram,
              twitter: !!contactToEdit.socialLinks?.twitter,
              linkedin: !!contactToEdit.socialLinks?.linkedin,
              facebook: !!contactToEdit.socialLinks?.facebook,
              youtube: !!contactToEdit.socialLinks?.youtube,
              website: !!contactToEdit.socialLinks?.website,
            }
          : {
              phones: true,
              emails: true,
              instagram: false,
              twitter: false,
              linkedin: false,
              facebook: false,
              youtube: false,
              website: false,
            },
      avatarFile: null,
    });
  }, [mode, contactToEdit, isOpen]);

  // Separate effect to handle contactToEdit changes for edit mode
  useEffect(() => {
    if (mode === "edit" && contactToEdit) {
      setFirstName(contactToEdit.firstName || contactToEdit.first_name || "");
      setLastName(contactToEdit.lastName || contactToEdit.last_name || "");
      setCompanyName(contactToEdit.company || contactToEdit.company_name || "");
      setAbout(contactToEdit.note?.content || "");
      setPhones(
        contactToEdit.phones && contactToEdit.phones.length > 0
          ? contactToEdit.phones
              .filter((phone) => phone !== null && phone.phone)
              .map((phone) => phone.phone)
          : [""]
      );
      setEmail(
        contactToEdit.email ||
          (contactToEdit.emails && contactToEdit.emails.length > 0
            ? contactToEdit.emails
                .filter((email) => email !== null && email.email)
                .map((email) => email.email)[0] || ""
            : "") ||
          ""
      );
      setAddress({
        country: contactToEdit.address?.country || "",
        city: contactToEdit.address?.city || "",
        state: contactToEdit.address?.state || "",
        zipCode: contactToEdit.address?.zip_code || "",
        line1: contactToEdit.address?.line_1 || "",
      });

      // Set full address for autocomplete
      const fullAddr = [
        contactToEdit.address?.line_1,
        contactToEdit.address?.city,
        contactToEdit.address?.state,
        contactToEdit.address?.zip_code,
      ]
        .filter(Boolean)
        .join(", ");
      setFullAddress(fullAddr);
      setUseManualAddress(fullAddr === "");

      setSocialLinks({
        instagram: contactToEdit.socialLinks?.instagram || "",
        twitter: contactToEdit.socialLinks?.twitter || "",
        linkedin: contactToEdit.socialLinks?.linkedin || "",
        facebook: contactToEdit.socialLinks?.facebook || "",
        youtube: contactToEdit.socialLinks?.youtube || "",
      });
      setWebsite(contactToEdit.socialLinks?.website || "");
      setAvatarPreview(contactToEdit.pictureBlob || null);

      // Show fields that have data
      setShowFields({
        phones: true,
        emails: true,
        instagram: !!contactToEdit.socialLinks?.instagram,
        twitter: !!contactToEdit.socialLinks?.twitter,
        linkedin: !!contactToEdit.socialLinks?.linkedin,
        facebook: !!contactToEdit.socialLinks?.facebook,
        youtube: !!contactToEdit.socialLinks?.youtube,
        website: !!contactToEdit.socialLinks?.website,
      });
    }
  }, [contactToEdit, mode]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // Check if phone number already exists (excluding the current index)
  const isPhoneDuplicate = (phone: string, excludeIndex?: number) => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) return false;

    return phones.some((existingPhone, index) => {
      if (excludeIndex !== undefined && index === excludeIndex) return false;
      return existingPhone.trim() === trimmedPhone;
    });
  };

  const handlePhoneChange = (index: number, value: string) => {
    // Check for duplicates when the value changes
    if (value.trim() && isPhoneDuplicate(value, index)) {
      toast.error("This phone number already exists");
      return;
    }
    setPhones((prev) => prev.map((p, i) => (i === index ? value : p)));
  };

  const handleRemovePhone = (index: number) => {
    setPhones((prev) => prev.filter((_, i) => i !== index));
    if (phones.length === 1)
      setShowFields((prev) => ({ ...prev, phones: false }));
  };

  const handleAddField = (field: string) => {
    setShowAddMenu(false);
    setShowFields((prev) => ({ ...prev, [field]: true }));
    if (field === "phones") setPhones((prev) => [...prev, ""]);
  };

  const handleRemoveSocial = (field: string) => {
    setShowFields((prev) => ({ ...prev, [field]: false }));
    setSocialLinks((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async () => {
    try {
      setIsUploading(true);

      // Basic validation
      if (!firstName && !lastName) {
        toast.error("Please enter at least a first name or last name");
        return;
      }

      // Phone validation - phone is mandatory
      const hasValidPhone = phones.some((phone) => phone.trim() !== "");
      if (!hasValidPhone) {
        toast.error("Please enter at least one phone number");
        return;
      }

      // Check for duplicate phone numbers
      const validPhones = phones.filter((phone) => phone.trim() !== "");
      const uniquePhones = new Set(validPhones.map((phone) => phone.trim()));
      if (uniquePhones.size !== validPhones.length) {
        toast.error("Duplicate phone numbers are not allowed");
        return;
      }

      // Format all phone numbers using centralized utility function
      const formattedPhones = phones
        .filter((p) => p.trim() !== "")
        .map((p) => ensurePhoneNumberFormat(p));

      console.log("handleSubmit debug:", {
        phones,
        formattedPhones,
        initialPhones: initialContactData?.phones,
      });

      const filteredEmails = email.trim() !== "" ? [email] : [];

      let pictureUrl: string | null = null;

      if (mode === "add") {
        // Create new contact with phone as single string
        const firstPhone = formattedPhones.length > 0 ? formattedPhones[0] : "";
        const firstEmail = filteredEmails.length > 0 ? filteredEmails[0] : "";

        const addContactPayload = {
          firstName: firstName || null,
          lastName: lastName || null,
          company: companyName || null,
          about: about || null,
          status: "ACTIVE",
          note: about || null,
          picture: null,
          email: firstEmail || null,
          address: {
            country: address.country || null,
            city: address.city || null,
            state: address.state || null,
            line_1: address.line1 || null,
            line_2: null,
            zip_code: address.zipCode || null,
          },
          socialLinks: {
            website: website || null,
            facebook: socialLinks.facebook || null,
            twitter: socialLinks.twitter || null,
            linkedin: socialLinks.linkedin || null,
            instagram: socialLinks.instagram || null,
            youtube: socialLinks.youtube || null,
          },
          phone: firstPhone || null,
        };

        const addContactResponse = await contactsApi.addContact(
          addContactPayload as any
        );

        console.log("addContactResponse", addContactResponse.data);
        console.log("addContactResponse.data.id", addContactResponse.data.id);

        // Add additional phone numbers to the contact if any exist
        if (formattedPhones.length > 1) {
          const additionalPhones = formattedPhones.slice(1);
          for (const phone of additionalPhones) {
            try {
              await contactsApi.addPhoneToContact(
                addContactResponse.data.id,
                phone
              );
            } catch (error) {
              console.error(
                "Error adding additional phone number:",
                phone,
                error
              );
              // Continue with other phones even if one fails
            }
          }
        }

        // If there's an avatar file, upload it
        if (avatarFile) {
          try {
            const pictureResponse = await contactsApi.addUpdateContactPicture(
              addContactResponse.data.id,
              avatarFile
            );
            pictureUrl = pictureResponse.data.picture;
          } catch (error) {
            console.error("Error uploading contact picture:", error);
            toast.error("Failed to upload picture. Please try again.");
            return;
          }
        }

        // Update contacts list with the new contact
        const newContact = { ...addContactResponse.data, picture: pictureUrl };
        setContacts((prevContacts) => [...prevContacts, newContact]);
        setSelectedContact(newContact);
        toast.success("Contact added successfully");

        // Refetch contacts to ensure data is fresh
        if (onRefetch) {
          onRefetch();
        }
      } else {
        // Edit existing contact
        if (!contactToEdit?.id) {
          toast.error("No contact selected for editing");
          return;
        }

        const changedFields = getChangedFields();
        const operations: Promise<any>[] = [];

        // Handle avatar upload if changed
        if (changedFields.avatar && avatarFile) {
          const avatarOperation = contactsApi
            .addUpdateContactPicture(String(contactToEdit.id), avatarFile)
            .then((response) => {
              pictureUrl = response.data.picture;
              return { type: "avatar", success: true, data: response.data };
            })
            .catch((error) => {
              console.error("Error uploading contact picture:", error);
              toast.error("Failed to upload picture");
              throw error;
            });
          operations.push(avatarOperation);
        }

        // Handle basic fields update if changed
        if (changedFields.basic) {
          // Only send changed fields, set unchanged to null
          const buildChangedContactData = () => {
            const changedData: any = {};

            // Compare each field with initial data
            if (firstName !== initialContactData.firstName) {
              changedData.firstName = firstName || null;
            }
            if (lastName !== initialContactData.lastName) {
              changedData.lastName = lastName || null;
            }
            if (companyName !== initialContactData.companyName) {
              changedData.company = companyName || null;
            }
            if (about !== initialContactData.about) {
              changedData.about = about || null;
            }
            if (email !== initialContactData.email) {
              changedData.email = email || null;
            }

            // Address comparison
            const initialAddress = initialContactData.address || {};
            const currentAddress = {
              country: address.country || null,
              city: address.city || null,
              state: address.state || null,
              line_1: address.line1 || null,
              line_2: null,
              zip_code: address.zipCode || null,
            };

            if (
              currentAddress.country !== initialAddress.country ||
              currentAddress.city !== initialAddress.city ||
              currentAddress.state !== initialAddress.state ||
              currentAddress.line_1 !== initialAddress.line_1 ||
              currentAddress.zip_code !== initialAddress.zipCode
            ) {
              changedData.address = currentAddress;
            }

            // Social links comparison
            const initialSocialLinks = initialContactData.socialLinks || {};
            const currentSocialLinks = {
              website: website || null,
              facebook: socialLinks.facebook || null,
              twitter: socialLinks.twitter || null,
              linkedin: socialLinks.linkedin || null,
              instagram: socialLinks.instagram || null,
              youtube: socialLinks.youtube || null,
            };

            if (
              currentSocialLinks.website !== initialSocialLinks.website ||
              currentSocialLinks.facebook !== initialSocialLinks.facebook ||
              currentSocialLinks.twitter !== initialSocialLinks.twitter ||
              currentSocialLinks.linkedin !== initialSocialLinks.linkedin ||
              currentSocialLinks.instagram !== initialSocialLinks.instagram ||
              currentSocialLinks.youtube !== initialSocialLinks.youtube
            ) {
              changedData.socialLinks = currentSocialLinks;
            }

            // Always include required fields with current values
            changedData.status = "ACTIVE";
            changedData.note = about || null;
            changedData.picture = pictureUrl || null;

            return changedData;
          };

          const changedContactData = buildChangedContactData();
          console.log("Changed contact data:", changedContactData);

          const basicOperation = contactsApi
            .editContact(String(contactToEdit.id), changedContactData)
            .then(() => {
              return { type: "basic", success: true, data: changedContactData };
            })
            .catch((error) => {
              console.error("Error updating contact:", error);
              throw error;
            });
          operations.push(basicOperation);
        }

        // Handle phone changes if phones changed
        if (changedFields.phones) {
          console.log(
            "About to call handlePhoneChanges with formattedPhones:",
            formattedPhones
          );
          const phoneOperations = handlePhoneChanges(
            String(contactToEdit.id),
            formattedPhones
          );
          console.log(
            `Adding ${phoneOperations.length} phone operations to operations array`
          );
          operations.push(...phoneOperations);
        }

        // Execute all operations
        try {
          const results = await Promise.allSettled(operations);

          // Check for failures
          const failures = results.filter(
            (result) => result.status === "rejected"
          );
          const successes = results.filter(
            (result) => result.status === "fulfilled"
          );

          // Always refetch to ensure data consistency, even if some operations failed
          const operationTypes = results.map((r) =>
            r.status === "fulfilled" ? r.value.type : "failed"
          );
          console.log("About to refetch after operations:", {
            totalOperations: results.length,
            successes: successes.length,
            failures: failures.length,
            operationTypes,
          });
          console.log("Operation details:", operationTypes);

          // Add a small delay to ensure database operations are committed
          setTimeout(() => {
            if (onRefetch) {
              console.log("Calling onRefetch...");
              onRefetch();
            } else {
              console.log("onRefetch is not available");
            }
          }, 500);

          if (failures.length > 0) {
            console.error("Some operations failed:", failures);
            if (successes.length > 0) {
              toast.error(
                "Some updates failed, but others succeeded. Data has been refreshed."
              );
            } else {
              toast.error("All updates failed. Please check and try again.");
            }
            return;
          }

          // Update local state based on successful operations
          let finalContact: IContact = { ...contactToEdit };

          successes.forEach((result: any) => {
            if (result.value.type === "avatar") {
              finalContact = {
                ...finalContact,
                picture: result.value.data.picture,
              };
            } else if (result.value.type === "basic") {
              finalContact = { ...finalContact, ...result.value.data };
            }
            // Phone operations don't need to update local state as they will be refetched
          });

          // Update the contacts list
          setContacts((prevContacts) =>
            prevContacts.map((c) =>
              c.id === String(contactToEdit.id) ? finalContact : c
            )
          );
          setSelectedContact(finalContact);
          toast.success("Contact updated successfully");
        } catch (error) {
          console.error("Error in update operations:", error);
          toast.error("Failed to update contact. Please try again.");
        }
      }

      onClose();
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error(`Failed to ${mode} contact. Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  const getDisplayName = () => {
    if (mode === "edit" && contactToEdit) {
      return (
        `${contactToEdit.first_name || ""} ${
          contactToEdit.last_name || ""
        }`.trim() || "Full Name"
      );
    }
    return firstName || lastName ? `${firstName} ${lastName}` : "Full Name";
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-8">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black opacity-30"
              onClick={onClose}
            ></div>

            {/* Modal */}
            <div className="relative w-full max-w-3xl rounded-lg bg-white px-6 py-8 shadow-lg flex flex-col md:flex-row gap-8 max-h-[90vh]">
              {/* Modal Header */}
              <div className="absolute top-0 left-0 w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg z-10">
                <h2 className="text-lg font-semibold">
                  {mode === "add" ? "Add New Contact" : "Edit Contact"}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <div className="pt-16 flex w-full md:flex-row gap-8">
                {/* Left: Avatar - Fixed */}
                <div className="flex flex-col items-center w-full md:w-1/3 border-r border-gray-200 pr-6 flex-shrink-0">
                  <div className="mb-4">
                    <div className="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden relative">
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="Avatar"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          className="w-16 h-16 text-gray-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4a4 4 0 110 8 4 4 0 010-8zm0 12c-4.418 0-8 1.79-8 4v2h16v-2c0-2.21-3.582-4-8-4z"
                          />
                        </svg>
                      )}
                      {isUploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      id="avatar-upload"
                      className="hidden"
                      onChange={handleAvatarChange}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="avatar-upload"
                      className={`mt-3 inline-block cursor-pointer rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                        isUploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {avatarFile ? "Change" : "Upload"} Avatar
                    </label>
                  </div>
                  <div className="text-lg font-semibold mt-2">
                    {getDisplayName()}
                  </div>

                  <div className="mt-auto pt-4 flex items-center relative add-menu-container">
                    <button
                      type="button"
                      className="flex items-center text-green-600 border border-green-600 rounded px-3 py-1 hover:bg-green-50"
                      onClick={() => setShowAddMenu((prev) => !prev)}
                    >
                      <FiPlus className="mr-1" /> Add
                    </button>
                    {showAddMenu && (
                      <div className="absolute z-20 bottom-full left-0 mb-2 bg-white border rounded shadow-lg p-2 flex flex-col min-w-[180px]">
                        <button
                          className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                          onClick={() => handleAddField("instagram")}
                          disabled={showFields.instagram}
                        >
                          <FaInstagram className="mr-2" /> Instagram
                        </button>
                        <button
                          className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                          onClick={() => handleAddField("twitter")}
                          disabled={showFields.twitter}
                        >
                          <FaXTwitter className="mr-2" /> X
                        </button>
                        <button
                          className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                          onClick={() => handleAddField("linkedin")}
                          disabled={showFields.linkedin}
                        >
                          <FaLinkedin className="mr-2" /> LinkedIn
                        </button>
                        <button
                          className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                          onClick={() => handleAddField("facebook")}
                          disabled={showFields.facebook}
                        >
                          <FaFacebook className="mr-2" /> Facebook
                        </button>
                        <button
                          className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                          onClick={() => handleAddField("youtube")}
                          disabled={showFields.youtube}
                        >
                          <FaYoutube className="mr-2" /> YouTube
                        </button>
                        <button
                          className="flex items-center px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
                          onClick={() => handleAddField("website")}
                          disabled={showFields.website}
                        >
                          <FiPlus className="mr-2" /> Website
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {/* Right: Details - Scrollable */}
                <div className="flex-1 w-full md:w-2/3 pl-0 px-4 md:pl-6 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="last name"
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2 flex gap-2 items-end">
                      {/* Phones */}
                      {showFields.phones && (
                        <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Phone(s)
                          </label>
                          {phones.map((phone, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 mb-1"
                            >
                              <PhoneInput
                                value={phone}
                                onChange={(value) =>
                                  handlePhoneChange(idx, value)
                                }
                                defaultCountry="us"
                                className="flex-1"
                                inputStyle={{
                                  width: "100%",
                                  padding: "12px",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  outline: "none",
                                }}
                                countrySelectorStyleProps={{
                                  buttonStyle: {
                                    padding: "12px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px 0 0 6px",
                                    outline: "none",
                                  },
                                }}
                              />
                              {phones.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemovePhone(idx)}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  &#x2715;
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              // Check if there's already an empty phone field
                              const hasEmptyPhone = phones.some(
                                (phone) => !phone.trim()
                              );
                              if (hasEmptyPhone) {
                                toast.error(
                                  "Please fill in the empty phone field first"
                                );
                                return;
                              }
                              setPhones((prev) => [...prev, ""]);
                            }}
                            className="text-blue-600 hover:underline text-sm w-fit"
                          >
                            + Add Phone
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                      {/* Emails */}
                      {showFields.emails && (
                        <label className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                      )}
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Company
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address
                      </label>

                      {/* Toggle between autocomplete and manual input */}
                      <div className="mb-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={useManualAddress}
                            onChange={(e) =>
                              setUseManualAddress(e.target.checked)
                            }
                            className="w-4 h-4 text-green-600 focus:ring-green-500"
                          />
                          <span className="ml-2 text-sm">
                            Enter Address Manually
                          </span>
                        </label>
                      </div>

                      {/* Google Maps Autocomplete */}
                      {!useManualAddress && (
                        <AddressAutocomplete
                          value={fullAddress}
                          onChange={setFullAddress}
                          onAddressSelect={handleAddressSelect}
                          placeholder="Enter your address"
                          className="w-full"
                        />
                      )}

                      {/* Manual Address Input */}
                      {useManualAddress && (
                        <input
                          type="text"
                          value={address.line1}
                          onChange={(e) =>
                            setAddress({ ...address, line1: e.target.value })
                          }
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter street address"
                        />
                      )}
                    </div>
                    {/* Manual Address Fields - Only show when manual address is selected */}
                    {useManualAddress && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            City
                          </label>
                          <input
                            type="text"
                            value={address.city}
                            onChange={(e) =>
                              setAddress({ ...address, city: e.target.value })
                            }
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            State
                          </label>
                          <input
                            type="text"
                            value={address.state}
                            onChange={(e) =>
                              setAddress({ ...address, state: e.target.value })
                            }
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter state"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Zip Code
                          </label>
                          <input
                            type="text"
                            value={address.zipCode}
                            onChange={(e) =>
                              setAddress({
                                ...address,
                                zipCode: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter zip code"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Country
                          </label>
                          <input
                            type="text"
                            value={address.country}
                            onChange={(e) =>
                              setAddress({
                                ...address,
                                country: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter country"
                          />
                        </div>
                      </>
                    )}
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Website
                      </label>
                      <input
                        type="text"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter website URL"
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Social Links
                      </label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {/* Social Links */}
                        {showFields.instagram && (
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                            <FaInstagram className="text-pink-500" />
                            <input
                              type="text"
                              value={socialLinks.instagram}
                              onChange={(e) =>
                                setSocialLinks({
                                  ...socialLinks,
                                  instagram: e.target.value,
                                })
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="Instagram"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveSocial("instagram")}
                              className="text-red-500 hover:text-red-700"
                            >
                              &#x2715;
                            </button>
                          </div>
                        )}
                        {showFields.twitter && (
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                            <FaXTwitter className="text-black" />
                            <input
                              type="text"
                              value={socialLinks.twitter}
                              onChange={(e) =>
                                setSocialLinks({
                                  ...socialLinks,
                                  twitter: e.target.value,
                                })
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="X (Twitter)"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveSocial("twitter")}
                              className="text-red-500 hover:text-red-700"
                            >
                              &#x2715;
                            </button>
                          </div>
                        )}
                        {showFields.linkedin && (
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                            <FaLinkedin className="text-blue-700" />
                            <input
                              type="text"
                              value={socialLinks.linkedin}
                              onChange={(e) =>
                                setSocialLinks({
                                  ...socialLinks,
                                  linkedin: e.target.value,
                                })
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="LinkedIn"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveSocial("linkedin")}
                              className="text-red-500 hover:text-red-700"
                            >
                              &#x2715;
                            </button>
                          </div>
                        )}
                        {showFields.facebook && (
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                            <FaFacebook className="text-blue-600" />
                            <input
                              type="text"
                              value={socialLinks.facebook}
                              onChange={(e) =>
                                setSocialLinks({
                                  ...socialLinks,
                                  facebook: e.target.value,
                                })
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="Facebook"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveSocial("facebook")}
                              className="text-red-500 hover:text-red-700"
                            >
                              &#x2715;
                            </button>
                          </div>
                        )}
                        {showFields.youtube && (
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                            <FaYoutube className="text-red-600" />
                            <input
                              type="text"
                              value={socialLinks.youtube}
                              onChange={(e) =>
                                setSocialLinks({
                                  ...socialLinks,
                                  youtube: e.target.value,
                                })
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="YouTube"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveSocial("youtube")}
                              className="text-red-500 hover:text-red-700"
                            >
                              &#x2715;
                            </button>
                          </div>
                        )}
                        {showFields.website && (
                          <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                            <FaGlobe className="text-blue-600" />
                            <input
                              type="text"
                              value={website}
                              onChange={(e) => setWebsite(e.target.value)}
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="Website"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowFields((prev) => ({
                                  ...prev,
                                  website: false,
                                }))
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              &#x2715;
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        About
                      </label>
                      <textarea
                        value={about}
                        onChange={(e) => setAbout(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Write about yourself"
                        rows={3}
                      />
                    </div>
                  </div>
                  {/* Footer */}
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={onClose}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isUploading || !hasChanges()}
                      className={`rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 ${
                        isUploading || !hasChanges()
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isUploading
                        ? mode === "add"
                          ? "Adding..."
                          : "Saving..."
                        : !hasChanges()
                        ? "No Changes"
                        : mode === "add"
                        ? "Add Contact"
                        : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
